/* eslint-disable max-lines */
import {
  BottomSheetWithIntent,
  CbhIcon,
  Divider,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Collapse, DialogContent, Stack } from "@mui/material";
import { TimePicker } from "@src/appV2/redesign/components/TimePicker";
import { addHours, addMinutes, isAfter, isBefore } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "../../../components/Button";
import { DialogFooter } from "../../../components/DialogFooter";
import { type Duration } from "../../../DateTime/DurationInput";
import { DurationInput } from "../../../DateTime/DurationInput";
import { formatTimeWithTimezone } from "../../../utils/formatTimeWithTimezone";
import { inferDateForAmbiguousBreakStartTime } from "./utils/inferDateForAmbiguousBreakStartTime";
import { getLocalTimeForPicker, getUtcTimeFromPicker } from "./utils/timezoneConversion";

interface EditBreakTimeFormValues {
  breakStart?: Date;
  breakDuration: Duration;
}

interface EditBreakTimeBottomSheetProps {
  modalState: UseModalState;
  breakStartTime?: Date;
  defaultBreakDuration: Duration;
  breakDuration?: Duration;
  initialBreakSkipped?: boolean;
  workplaceTimezone: string;
  clockInTime: Date;
  clockOutTime: Date;
  onConfirm: (breakStartTime: Date | undefined, breakDuration: Duration) => void;
  onCancel?: () => void;
}

type ActiveSelector = "breakStart" | "breakDuration" | undefined;

// Helper function to format duration, showing "Skipped" when break is skipped
function formatBreakDuration(duration: Duration, isSkipped: boolean): string {
  if (isSkipped) {
    return "Skipped";
  }

  if (duration.hours > 0) {
    const minutesFormatted = duration.minutes.toString().padStart(2, "0");
    return `${duration.hours} hr ${minutesFormatted} min`;
  }

  return `${duration.minutes} min`;
}

// Helper function to calculate middle of shift
function calculateMiddleOfShift(clockInTime: Date, clockOutTime: Date): Date {
  const shiftDurationMs = clockOutTime.getTime() - clockInTime.getTime();
  return new Date(clockInTime.getTime() + shiftDurationMs / 2);
}

function formatBreakStartDisplay(
  breakSkipped: boolean,
  currentLocalBreakStartTime?: Date,
  workplaceTimezone?: string
): string {
  if (breakSkipped) {
    return "--:-- --"; // Show N/A for skipped breaks
  }

  if (isDefined(currentLocalBreakStartTime) && isDefined(workplaceTimezone)) {
    // Convert local time back to UTC, then format in workplace timezone
    const utcTime = getUtcTimeFromPicker(currentLocalBreakStartTime, workplaceTimezone);
    return formatTimeWithTimezone(utcTime, workplaceTimezone, false);
  }

  return "--:-- --";
}

export function EditBreakTimeBottomSheet(props: EditBreakTimeBottomSheetProps) {
  const {
    modalState,
    breakStartTime,
    defaultBreakDuration,
    breakDuration = defaultBreakDuration,
    initialBreakSkipped = false, // Default to false
    workplaceTimezone,
    clockInTime,
    clockOutTime,
    onConfirm,
    onCancel,
  } = props;

  const [activeSelector, setActiveSelector] = useState<ActiveSelector>();
  const [localBreakSkipped, setLocalBreakSkipped] = useState(initialBreakSkipped);
  const [saveAttempted, setSaveAttempted] = useState(false); // Track if save was attempted
  const [hasUserChangedTime, setHasUserChangedTime] = useState(false);

  // Calculate default break start time for skipped breaks
  const getDefaultBreakStartTime = useCallback(() => {
    if (breakStartTime) {
      // Use existing break start time
      return getLocalTimeForPicker(breakStartTime, workplaceTimezone);
    }

    if (initialBreakSkipped) {
      // For skipped breaks, default picker to middle of shift
      const middleTime = calculateMiddleOfShift(clockInTime, clockOutTime);
      return getLocalTimeForPicker(middleTime, workplaceTimezone);
    }

    return undefined;
  }, [breakStartTime, initialBreakSkipped, clockInTime, clockOutTime, workplaceTimezone]);

  const { setValue, watch, reset } = useForm<EditBreakTimeFormValues>({
    defaultValues: {
      breakStart: getDefaultBreakStartTime(),
      breakDuration,
    },
    mode: "onSubmit",
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      reset({
        breakStart: getDefaultBreakStartTime(),
        breakDuration,
      });
      setLocalBreakSkipped(initialBreakSkipped);
      setSaveAttempted(false);
      setActiveSelector(undefined);
      setHasUserChangedTime(false);
    }
  }, [modalState.modalIsOpen, reset, getDefaultBreakStartTime, breakDuration, initialBreakSkipped]);

  const currentLocalBreakStartTime = watch("breakStart");
  const currentBreakDuration = watch("breakDuration");

  // Convert back to UTC for validation and submission
  const currentBreakStartTime = currentLocalBreakStartTime
    ? getUtcTimeFromPicker(currentLocalBreakStartTime, workplaceTimezone)
    : undefined;

  const calculateBreakEndTime = (startTime?: Date, duration?: Duration): Date | undefined => {
    if (!isDefined(startTime) || !isDefined(duration) || localBreakSkipped) {
      return undefined;
    }

    let endTime = new Date(startTime);
    if (duration.hours > 0) {
      endTime = addHours(endTime, duration.hours);
    }

    if (duration.minutes > 0) {
      endTime = addMinutes(endTime, duration.minutes);
    }

    return endTime;
  };

  const getErrorMessage = (startTime?: Date, duration?: Duration): string | undefined => {
    // No validation needed for skipped breaks
    if (localBreakSkipped) {
      return undefined;
    }

    if (!isDefined(startTime)) {
      return undefined;
    }

    // Apply inference to resolve ambiguous times for overnight shifts before validation
    const timeToValidate =
      hasUserChangedTime && breakStartTime
        ? inferDateForAmbiguousBreakStartTime(startTime, breakStartTime, {
            earliestAllowed: clockInTime,
            latestAllowed: clockOutTime,
          })
        : startTime;

    const endTime = calculateBreakEndTime(timeToValidate, duration);

    if (isBefore(timeToValidate, clockInTime)) {
      return "You cannot start your break before your clock-in time.";
    }

    if (isAfter(timeToValidate, clockOutTime)) {
      return "You cannot start your break after your clock-out time.";
    }

    if (isDefined(endTime) && isAfter(endTime, clockOutTime)) {
      return "You cannot end your break after your clock-out time.";
    }

    return undefined;
  };

  // Only show error if save was attempted
  const errorMessage = saveAttempted
    ? getErrorMessage(currentBreakStartTime, currentBreakDuration)
    : undefined;

  const handleConfirm = () => {
    setSaveAttempted(true); // Mark that save was attempted

    // For skipped breaks, pass undefined start time and 0 duration
    if (localBreakSkipped) {
      onConfirm(undefined, { hours: 0, minutes: 0 });
      modalState.closeModal();
      return;
    }

    // Check for errors on save
    const currentError = getErrorMessage(currentBreakStartTime, currentBreakDuration);

    if (!isDefined(currentBreakStartTime)) {
      // Don't close, let user see that start time is required
      return;
    }

    if (currentError) {
      // Don't close, let user see the error
      return;
    }

    // Apply inference only if user changed the time
    const timeToSave =
      hasUserChangedTime && breakStartTime
        ? inferDateForAmbiguousBreakStartTime(currentBreakStartTime, breakStartTime, {
            earliestAllowed: clockInTime,
            latestAllowed: clockOutTime,
          })
        : currentBreakStartTime;

    // No errors, proceed with save (convert back to UTC for parent)
    onConfirm(timeToSave, currentBreakDuration);
    modalState.closeModal();
  };

  const handleCancel = () => {
    onCancel?.();
    modalState.closeModal();
  };

  const handleTimeChange = useCallback(
    (newTime: Date | undefined) => {
      if (isDefined(newTime)) {
        setValue("breakStart", newTime);
        setHasUserChangedTime(true);

        // If this was a skipped break and user is modifying time, automatically:
        // 1. Set break duration to 30 minutes
        // 2. Mark break as no longer skipped
        if (localBreakSkipped) {
          setValue("breakDuration", defaultBreakDuration);
          setLocalBreakSkipped(false);
        }

        // Clear save attempted flag when user makes changes
        setSaveAttempted(false);
      }
    },
    [localBreakSkipped, defaultBreakDuration, setValue]
  );

  const handleDurationChange = (newDuration: Duration) => {
    setValue("breakDuration", newDuration);

    const isNewDurationZero = newDuration.hours === 0 && newDuration.minutes === 0;

    // If break starts as skipped but HCP selects non-zero duration
    if (localBreakSkipped && !isNewDurationZero) {
      // Automatically force the break_start_at to be the middle of the shift
      const middleTime = calculateMiddleOfShift(clockInTime, clockOutTime);
      // Convert UTC middle time to local time for the picker
      const localMiddleTime = getLocalTimeForPicker(middleTime, workplaceTimezone);
      setValue("breakStart", localMiddleTime);
      setHasUserChangedTime(false); // This is automatic, not user-changed

      setLocalBreakSkipped(false);
    }
    // If break starts as NON-skipped, selecting the 'Skipped' option (duration = 0)
    else if (!localBreakSkipped && isNewDurationZero) {
      // Force the 'Break start' timestamp to render as N/A
      setValue("breakStart", undefined);
      setLocalBreakSkipped(true);
    }

    // Clear save attempted flag when user makes changes
    setSaveAttempted(false);
  };

  const toggleSelector = (selector: ActiveSelector) => {
    setActiveSelector(activeSelector === selector ? undefined : selector);
  };

  useEffect(() => {
    if (activeSelector === "breakStart" && localBreakSkipped) {
      handleTimeChange(getDefaultBreakStartTime());
    }
  }, [activeSelector, localBreakSkipped, handleTimeChange, getDefaultBreakStartTime]);

  // For skipped breaks, we're always valid. For non-skipped, need start time and no errors
  const isValid = localBreakSkipped || (isDefined(currentBreakStartTime) && !errorMessage);

  return (
    <BottomSheetWithIntent
      modalState={modalState}
      intent={errorMessage ? "error" : undefined}
      intentContent={errorMessage}
      intentIcon={errorMessage ? "triangle-exclamation" : undefined}
      footer={
        <DialogFooter orientation="vertical" onClose={handleCancel}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isValid}
            onClick={handleConfirm}
          >
            Save
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={6}>
          <Title semibold variant="h4" component="h2">
            Edit break
          </Title>

          <Stack spacing={4}>
            <Stack direction="row" spacing={3} alignItems="center">
              <CbhIcon size="medium" type="break" />
              <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
                Break start
              </Text>
              <Box textAlign="right">
                <Button
                  aria-label="Select Break start"
                  variant="outlined"
                  size="small"
                  endIconType="pencil"
                  isSelected={activeSelector === "breakStart"}
                  onClick={() => {
                    toggleSelector("breakStart");
                  }}
                >
                  {formatBreakStartDisplay(
                    localBreakSkipped,
                    currentLocalBreakStartTime,
                    workplaceTimezone
                  )}
                </Button>
              </Box>
            </Stack>

            <Stack direction="row" spacing={3} alignItems="center">
              <CbhIcon size="medium" type="clock" />
              <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
                Break duration
              </Text>
              <Box textAlign="right">
                <Button
                  aria-label="Select Break duration"
                  variant="outlined"
                  size="small"
                  endIconType="pencil"
                  isSelected={activeSelector === "breakDuration"}
                  onClick={() => {
                    toggleSelector("breakDuration");
                  }}
                >
                  {formatBreakDuration(currentBreakDuration, localBreakSkipped)}
                </Button>
              </Box>
            </Stack>

            <Collapse in={activeSelector === "breakStart"} timeout={300}>
              <Stack spacing={2}>
                <Divider />
                <TimePicker
                  ampm
                  skipDisabled
                  preventParentTouchStart
                  value={currentLocalBreakStartTime}
                  timeSteps={{ minutes: 1 }}
                  containerSx={{
                    // make sure the time picker takes as little horizontal space as possible
                    // this preserves more area on the left/right for the bottom sheet to capture touch events
                    // for swipe-down closing (dismiss)
                    alignSelf: "center",
                  }}
                  sx={{
                    justifyContent: "center",
                    "& .MuiList-root": {
                      scrollbarWidth: "none",
                    },
                  }}
                  onChange={handleTimeChange}
                />
              </Stack>
            </Collapse>

            <Collapse in={activeSelector === "breakDuration"} timeout={300}>
              <Stack spacing={2}>
                <Box textAlign="center">
                  <Button
                    variant={localBreakSkipped ? "contained" : "outlined"}
                    size="small"
                    onClick={() => {
                      setLocalBreakSkipped(true);
                      setValue("breakDuration", { hours: 0, minutes: 0 });
                      setValue("breakStart", undefined);
                      setSaveAttempted(false);
                    }}
                  >
                    Skipped break
                  </Button>
                </Box>

                <DurationInput
                  preventParentTouchStart
                  value={currentBreakDuration}
                  maxHours={4}
                  minutesStep={5}
                  onChange={handleDurationChange}
                />
              </Stack>
            </Collapse>
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheetWithIntent>
  );
}
/* eslint-enable max-lines */
